import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, MessageCircle, CalendarClock, Mail, MapPin, Phone } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/pages/Home";
import { SERVICES } from "@/data/services";
import api, { formatApiErrorDetail } from "@/lib/api";

const WHATSAPP = "https://wa.me/34696990879";
const PHONE = "+34696990879";
const PHONE_DISPLAY = "696 99 08 79";
const CALENDAR = "https://calendly.com/hosys/demo";

export default function Contact() {
  const { lang, t, ml } = useI18n();
  useSEO({ lang, title: `${t("nav.contact")} | HO-SYS`, description: t("contact.subtitle"), path: "/contact" });

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, lang });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-neon/60 transition-colors";
  const labelCls = "block font-mono text-xs uppercase tracking-widest text-white/50 mb-2";

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <SectionLabel>{t("nav.contact")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-3xl">
            {t("contact.title")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">{t("contact.subtitle")}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:p-9 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t("contact.name")}</label>
                  <input data-testid="contact-name" required value={form.name} onChange={set("name")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("contact.email")}</label>
                  <input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t("contact.phone")}</label>
                  <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t("contact.company")}</label>
                  <input data-testid="contact-company" value={form.company} onChange={set("company")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("contact.service")}</label>
                <select data-testid="contact-service" value={form.service} onChange={set("service")} className={inputCls}>
                  <option value="">{t("contact.selectService")}</option>
                  {SERVICES.map((s) => <option key={s.slug} value={ml(s.title)}>{ml(s.title)}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("contact.message")}</label>
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls + " resize-none"} />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={sending}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-neon px-7 py-4 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60 w-full sm:w-auto">
                {sending ? t("contact.sending") : t("contact.send")}
                <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5 space-y-4">
            <a href={CALENDAR} target="_blank" rel="noreferrer" data-testid="contact-calendar"
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-neon/15 border border-neon/30 flex items-center justify-center shrink-0">
                <CalendarClock className="w-5 h-5 text-neon" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-white">{t("contact.calendar")}</h3>
                <p className="text-white/50 text-sm mt-1">calendly.com/osis</p>
              </div>
            </a>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-lime/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-lime/15 border border-lime/30 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-lime" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-white">{t("contact.whatsapp")}</h3>
                <p className="text-white/50 text-sm mt-1">{PHONE_DISPLAY}</p>
              </div>
            </a>
            <a href="mailto:info@hosys.es" data-testid="contact-email-link"
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-white">{t("contact.emailUs")}</h3>
                <p className="text-white/50 text-sm mt-1">info@hosys.es</p>
              </div>
            </a>
            <a href={`tel:${PHONE}`} data-testid="contact-phone-link"
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-neon/15 border border-neon/30 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-neon" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-white">{PHONE_DISPLAY}</h3>
                <p className="text-white/50 text-sm mt-1">Telèfon</p>
              </div>
            </a>
            <div className="rounded-2xl overflow-hidden border border-white/10 h-[220px]">
              <iframe title="HO-SYS map" width="100%" height="100%" loading="lazy" style={{ filter: "invert(90%) hue-rotate(180deg)" }}
                src="https://www.openstreetmap.org/export/embed.html?bbox=1.0%2C41.13%2C1.16%2C41.18&layer=mapnik&marker=41.1549%2C1.1069" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
